import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, User, ArrowRight } from 'lucide-react';
import { auth, ApiError, RateLimitError, type RegionSlug } from '../lib/apiClient';
import { passwordPasses } from '../lib/passwordPolicy';
import { validateEmail, validateFullName } from '../lib/profileValidation';
import { useAuth } from '../context/AuthContext';

type SignupFieldKey = 'full_name' | 'email';
type SignupFieldErrors = Partial<Record<SignupFieldKey, string>>;

const SIGNUP_FIELD_DOM_IDS: Record<SignupFieldKey, string> = {
  full_name: 'signup-field-full_name',
  email: 'signup-field-email',
};

// Local key allow-list — we deliberately don't extend VALID_PROFILE_FIELD_KEYS
// to include `email`, because email isn't a profile field (it's immutable post-
// signup; My Profile renders it read-only).
const BACKEND_TO_SIGNUP_KEY: Record<string, SignupFieldKey> = {
  full_name: 'full_name',
  email: 'email',
};

function signupFieldErrorsFromApiError(err: unknown): SignupFieldErrors {
  if (!(err instanceof ApiError) || !err.details) return {};
  const out: SignupFieldErrors = {};
  for (const detail of err.details) {
    if (!detail.field || !detail.issue) continue;
    const head = detail.field.split('.')[0];
    const key = BACKEND_TO_SIGNUP_KEY[head];
    if (key && !out[key]) out[key] = detail.issue;
  }
  return out;
}
import { AuthCard } from '../components/auth/AuthCard';
import { ErrorBanner } from '../components/auth/ErrorBanner';
import { PasswordField } from '../components/auth/PasswordField';
import { RegionSelect } from '../components/auth/RegionSelect';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { RateLimitInline, isRateLimited } from '../components/RateLimitBanner';
import { usePageTitle } from '../lib/usePageTitle';

export function SignUpPage() {
  usePageTitle('QuantumHire - Sign Up');
  const navigate = useNavigate();
  const { setCandidate, refresh } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [region, setRegion] = useState<RegionSlug | ''>('');
  const [error, setError] = useState<string | null>(null);
  // Recruitment workflow step 5f — the email already has an account (it may be one QuantumHire
  // created from the candidate's resume, which has no password yet). Shows Sign in / Forgot
  // password links under the message; the message itself is the backend's, generic for all.
  const [emailInUse, setEmailInUse] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  const canSubmit =
    !submitting &&
    fullName.trim() &&
    email.trim() &&
    passwordPasses(password) &&
    region &&
    !isRateLimited(rateLimitedUntil);

  const focusFirstError = (errors: SignupFieldErrors) => {
    const order: SignupFieldKey[] = ['full_name', 'email'];
    for (const key of order) {
      if (!errors[key]) continue;
      const el = document.getElementById(SIGNUP_FIELD_DOM_IDS[key]);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
      }
      break;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setEmailInUse(false);
    setFieldErrors({});

    // Client-side validation BEFORE the network call — surface field-level errors
    // with scroll-to-first, mirroring the My Profile / job-edit pattern. Google
    // signup intentionally skips this (handleGoogle below): name + email come
    // pre-verified from Google and shouldn't be run through these validators.
    const clientErrors: SignupFieldErrors = {};
    const nameErr = validateFullName(fullName);
    if (nameErr) clientErrors.full_name = nameErr;
    const emailErr = validateEmail(email);
    if (emailErr) clientErrors.email = emailErr;
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      focusFirstError(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await auth.signup({
        full_name: fullName.trim(),
        email: email.trim(),
        password,
        region_slug: region as RegionSlug,
      });
      // Phase 7-dashboard hotfix round 3 — signup is now email-verification-first.
      // We do NOT call setCandidate here. The candidate stays signed-out until they
      // click the verification link, which signs them in via /verify-email/:token.
      setSubmittedEmail(result.email);
      // Best-effort refresh — clears any stale auth state from a previous session.
      void refresh();
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        // Project backend `details[]` into per-field errors when present
        // (VALIDATION_FAILED with the canonical envelope). Falls back to the
        // top-level banner for non-validation errors (NOT_FOUND, conflict, etc.).
        const projected = signupFieldErrorsFromApiError(err);
        if (Object.keys(projected).length > 0) {
          setFieldErrors(projected);
          focusFirstError(projected);
        } else {
          setError(err.message);
          setEmailInUse(err.code === 'EMAIL_IN_USE');
        }
      } else {
        setError('Could not create your account. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = useCallback(
    async (idToken: string) => {
      if (!region) {
        setError('Pick a region before continuing with Google.');
        return;
      }
      setError(null);
      setEmailInUse(false);
      setSubmitting(true);
      try {
        const me = await auth.signupWithGoogle({
          id_token: idToken,
          region_slug: region as RegionSlug,
        });
        setCandidate(me);
        // Google verifies email, so go straight home.
        navigate('/');
      } catch (err) {
        if (err instanceof RateLimitError) {
          setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
        } else if (err instanceof ApiError) {
          setError(err.message);
          setEmailInUse(err.code === 'EMAIL_IN_USE');
        } else {
          setError('Could not sign you up with Google. Please try again.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [region, navigate, setCandidate],
  );

  if (submittedEmail) {
    return (
      <AuthCard
        publicTheme
        eyebrow="Check your inbox"
        title="One more step"
        subtitle={`We sent a verification link to ${submittedEmail}. Click it to confirm your email — then come back and start exploring jobs.`}
      >
        <div className="space-y-3">
          <Link
            to="/"
            className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
          >
            Back to home
          </Link>
          <p className="text-xs text-brand-muted text-center">
            Didn&apos;t receive it? Check your spam folder. Trying to sign in before verifying will
            resend the link automatically.
          </p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      publicTheme
      eyebrow="Create candidate account"
      title="Get matched with jobs that fit"
      subtitle="Sign up to apply, save jobs, and let companies discover you."
      footer={
        <>
          Already have an account?{' '}
          <Link
            to="/signin"
            className="text-brand-primary font-bold hover:text-brand-accent transition-colors"
          >
            Sign in
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />
      {emailInUse && (
        <p className="-mt-2 mb-4 text-sm text-brand-muted">
          <Link to="/signin" className="font-bold text-brand-primary hover:text-brand-accent">
            Sign in
          </Link>{' '}
          or{' '}
          <Link to="/forgot-password" className="font-bold text-brand-primary hover:text-brand-accent">
            set a password with Forgot password
          </Link>
          .
        </p>
      )}
      <RateLimitInline
        deadlineMs={rateLimitedUntil}
        className="text-sm text-amber-900 font-medium mb-3"
      />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div id={SIGNUP_FIELD_DOM_IDS.full_name}>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              required
              placeholder="Full name"
              autoComplete="name"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (fieldErrors.full_name) setFieldErrors((p) => ({ ...p, full_name: undefined }));
              }}
              aria-invalid={fieldErrors.full_name ? true : undefined}
              className={`w-full pl-12 pr-4 py-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium ${
                fieldErrors.full_name ? 'border-red-300' : 'border-gray-100'
              }`}
            />
          </div>
          {fieldErrors.full_name && (
            <p className="mt-1.5 text-xs text-red-700 font-medium">{fieldErrors.full_name}</p>
          )}
        </div>

        <div id={SIGNUP_FIELD_DOM_IDS.email}>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="email"
              required
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: undefined }));
              }}
              aria-invalid={fieldErrors.email ? true : undefined}
              className={`w-full pl-12 pr-4 py-4 bg-gray-50 border rounded-2xl focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all font-medium ${
                fieldErrors.email ? 'border-red-300' : 'border-gray-100'
              }`}
            />
          </div>
          {fieldErrors.email && (
            <p className="mt-1.5 text-xs text-red-700 font-medium">{fieldErrors.email}</p>
          )}
        </div>

        <PasswordField
          value={password}
          onChange={setPassword}
          showPolicy
          autoComplete="new-password"
          placeholder="Create a password"
        />

        <RegionSelect value={region} onChange={setRegion} />

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-brand-deep transition-all shadow-lg shadow-brand-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Creating account…' : 'Create account'}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>

      <GoogleSignInButton
        onCredential={handleGoogle}
        text="signup_with"
        disabled={!region || submitting}
      />
      {!region && (
        <p className="text-xs text-brand-muted text-center mt-3">
          Pick a region before continuing with Google.
        </p>
      )}
    </AuthCard>
  );
}
